import { useState } from "react";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { track } from "@/lib/analytics";

const BUSINESS_TYPES = ["Retail", "Restaurant", "Entertainment venue", "Office", "Warehouse", "Gym/fitness", "Other"];
const SCHEDULES = ["Monthly", "Every 2 months", "Quarterly", "Not sure, recommend one"];

const EMPTY = { business_name: "", contact_name: "", phone: "", email: "", business_type: "", city: "", num_locations: "", num_units: "", service_frequency: "", concerns: "" };

const QuoteSuccess = () => (
  <div data-testid="commercial-quote-success" className="bg-white border border-zinc-200 p-8 text-center rounded-sm">
    <CheckCircle2 className="w-12 h-12 text-brand mx-auto mb-4" />
    <h3 className="font-heading font-bold text-2xl text-brand-ink mb-2">Site visit requested</h3>
    <p className="text-zinc-600">Thanks! A commercial specialist will contact you to schedule your free site visit. Need us sooner? Call <a href="tel:7865121929" className="text-brand font-medium">786-512-1929</a>.</p>
  </div>
);

export const CommercialQuoteForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setVal = (k) => (v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/commercial-plans", form);
      track("commercial_lead_submit", { business_type: form.business_type, city: form.city });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (done) return <QuoteSuccess />;

  return (
    <form data-testid="commercial-quote-form" onSubmit={submit} className="bg-white border-2 border-brand p-6 sm:p-8 rounded-sm grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Input data-testid="cq-business-name" required placeholder="Business name" value={form.business_name} onChange={set("business_name")} className="rounded-sm" />
      <Input data-testid="cq-contact-name" required placeholder="Contact name" value={form.contact_name} onChange={set("contact_name")} className="rounded-sm" />
      <Input data-testid="cq-phone" required type="tel" placeholder="Phone" value={form.phone} onChange={set("phone")} className="rounded-sm" />
      <Input data-testid="cq-email" required type="email" placeholder="Email" value={form.email} onChange={set("email")} className="rounded-sm" />
      <Select value={form.business_type} onValueChange={setVal("business_type")} required>
        <SelectTrigger data-testid="cq-business-type" className="rounded-sm"><SelectValue placeholder="Business type" /></SelectTrigger>
        <SelectContent>{BUSINESS_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
      </Select>
      <Input data-testid="cq-city" required placeholder="City" value={form.city} onChange={set("city")} className="rounded-sm" />
      <Input data-testid="cq-num-locations" required type="number" min="1" placeholder="Number of locations" value={form.num_locations} onChange={set("num_locations")} className="rounded-sm" />
      <Input data-testid="cq-num-units" type="number" min="1" placeholder="Approx. number of AC units (optional)" value={form.num_units} onChange={set("num_units")} className="rounded-sm" />
      <div className="sm:col-span-2">
        <Select value={form.service_frequency} onValueChange={setVal("service_frequency")} required>
          <SelectTrigger data-testid="cq-schedule" className="rounded-sm"><SelectValue placeholder="Preferred visit schedule" /></SelectTrigger>
          <SelectContent>{SCHEDULES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <Textarea data-testid="cq-message" placeholder="Message (optional)" value={form.concerns} onChange={set("concerns")} className="sm:col-span-2 rounded-sm" rows={3} />
      {error && <p data-testid="cq-error" className="sm:col-span-2 text-sm text-brand-red">{error}</p>}
      <button data-testid="cq-submit-btn" type="submit" disabled={loading}
        className="sm:col-span-2 bg-brand hover:bg-brand-dark disabled:opacity-60 text-white font-bold py-4 rounded-sm transition-colors duration-200 flex items-center justify-center gap-2">
        {loading && <Loader2 className="w-4 h-4 animate-spin" />} Request a Free Site Visit
      </button>
    </form>
  );
};
